import { useState, type JSX } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2, Plus, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDiscoveryClient } from '../api/context';
import { ApiError, type ResolvedSound, type SuggestedSound, type SuggestedSoundsResponse } from '../api/types';
import { SoundLink } from '../components/SoundLink';

/**
 * Sounds to add to a TikTok sound search, under the sound box.
 *
 * Saved suggestions (campaign audio, sounds proven creators or a folder's
 * creators share, sounds that found people before) load for free. "Sounds in
 * videos about…" reads 1–2 keyword search pages, so it only runs on a click.
 * Every row previews in place and opens on TikTok; Add puts it in the search.
 */
export function SuggestedSounds({
  sounds,
  onAdd,
  topicTerms,
}: {
  sounds: ResolvedSound[];
  onAdd: (sound: ResolvedSound) => void;
  /** The form's topic words, to prefill the topic box. */
  topicTerms?: string[];
}): JSX.Element | null {
  const client = useDiscoveryClient();
  const [open, setOpen] = useState(true);
  const [topic, setTopic] = useState((topicTerms ?? []).join(', '));

  const saved = useQuery({
    queryKey: ['discovery', 'suggested-sounds', 'saved'],
    queryFn: () => client.sounds.suggest({}),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const topicRun = useMutation({
    mutationFn: (terms: string[]) =>
      client.sounds.suggest({ topicTerms: terms, exclude: sounds.map((s) => s.musicId) }),
  });

  // An older server has no suggest route: show nothing rather than an error.
  if (saved.error instanceof ApiError && saved.error.status === 404) return null;

  const added = new Set(sounds.map((s) => s.musicId));
  const groups = mergeGroups(saved.data, topicRun.data).map((g) => ({ ...g, sounds: g.sounds.filter((s) => !added.has(s.id)) }));
  const visible = groups.filter((g) => g.sounds.length > 0);
  const terms = topic.split(/[,\n]/).map((t) => t.trim()).filter(Boolean);

  return (
    <div className="rounded-lg border border-border" data-suggested-sounds>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium"
      >
        Suggested sounds
        <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="space-y-3 border-t border-border px-3 py-3">
          {saved.isLoading && <p className="text-xs text-muted-foreground">Loading suggestions…</p>}
          {visible.map((g) => (
            <section key={g.key} aria-label={g.label} className="space-y-1">
              <h4 className="text-xs font-semibold text-muted-foreground">{g.label}</h4>
              <ul className="grid gap-1">
                {g.sounds.map((s) => (
                  <SuggestionRow key={s.id} sound={s} onAdd={onAdd} />
                ))}
              </ul>
            </section>
          ))}
          {!saved.isLoading && visible.length === 0 && !topicRun.data && (
            <p className="text-xs text-muted-foreground">
              No saved sound suggestions yet. Campaign audio, sounds your proven creators share and sounds sent from
              Lookalikes show up here.
            </p>
          )}

          <form
            className="grid gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              if (terms.length > 0) topicRun.mutate(terms);
            }}
          >
            <label htmlFor="suggested-sounds-topic" className="text-xs font-semibold text-muted-foreground">
              Sounds in videos about…
            </label>
            <div className="flex gap-2">
              <Input
                id="suggested-sounds-topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="lip sync, pov"
                className="h-8"
              />
              <Button type="submit" variant="outline" size="sm" disabled={terms.length === 0 || topicRun.isPending}>
                {topicRun.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                Find sounds
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Reads 1–2 pages of a TikTok search (1–2 API calls) and lists the sounds those videos use, leaving out
              creators’ own voices.
            </p>
            {topicRun.data?.topicError && <p className="text-xs text-destructive-strong">{topicRun.data.topicError}</p>}
            {topicRun.error && <p className="text-xs text-destructive-strong">{(topicRun.error as Error).message}</p>}
            {topicRun.data && !topicRun.data.topicError && !topicRun.data.groups.some((g) => g.key === 'topic') && (
              <p className="text-xs text-muted-foreground">
                Those videos didn’t share any sounds worth adding — creators in talking formats mostly use their own
                voice. Try other words, or turn on Keep finding sounds.
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
}

function SuggestionRow({ sound: s, onAdd }: { sound: SuggestedSound; onAdd: (sound: ResolvedSound) => void }): JSX.Element {
  return (
    <li className="flex items-center gap-2 rounded-md border border-border/70 p-1.5" data-suggested-sound={s.id}>
      <SoundLink variant="row" platform="tiktok" id={s.id} title={s.title} />
      {s.uses != null && (
        <span className="shrink-0 text-xs text-muted-foreground" title={s.reason}>
          {s.uses} video{s.uses === 1 ? '' : 's'}
        </span>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="ml-auto h-8 shrink-0"
        aria-label={`Add ${s.title ?? `sound ${s.id}`}`}
        onClick={() => onAdd({ musicId: s.id, title: s.title, author: null, coverUrl: null, duration: null })}
      >
        <Plus className="h-3.5 w-3.5" />
        Add
      </Button>
    </li>
  );
}

/** Saved groups from the first load, plus the topic group from the latest topic run. */
function mergeGroups(saved: SuggestedSoundsResponse | undefined, topic: SuggestedSoundsResponse | undefined) {
  const base = saved?.groups ?? [];
  const topicGroup = topic?.groups.find((g) => g.key === 'topic');
  return topicGroup ? [...base, topicGroup] : base;
}
